import { createHmac } from 'crypto';

// 百度智能云 BCE 开放 API 签名 v1（bce-auth-v1，AK/SK HMAC-SHA256）
// 规范: https://cloud.baidu.com/doc/Reference/s/Njwvz1wot
// 与千帆通道的 Bearer API Key 鉴权无关；VOD 等 BCE 系 API 均使用本签名。

export type BceSignParams = {
    /** Access Key ID */
    ak: string;
    /** Secret Access Key */
    sk: string;
    /** HTTP 方法 */
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    /** 请求路径，如 /v2/desubtitle（不含查询串） */
    path: string;
    /** 查询参数（无需预编码） */
    query?: Record<string, string | number | undefined>;
    /** 请求域名，如 vod.bj.baidubce.com */
    host: string;
    /** 签名有效期（秒），默认 1800 */
    expirationSeconds?: number;
};

// RFC 3986 百分号编码：仅保留 A-Z a-z 0-9 - _ . ~
// 导出供调用方构建实际请求 URL，确保与签名的规范化编码一致（encodeURIComponent 不编码 !'()*）。
export function uriEncode(value: string, encodeSlash = true): string {
    const encoded = encodeURIComponent(value).replace(
        /[!'()*]/g,
        (ch) => `%${ch.charCodeAt(0).toString(16).toUpperCase()}`,
    );
    return encodeSlash ? encoded : encoded.replace(/%2F/gi, '/');
}

function hmacSha256Hex(key: string, message: string): string {
    return createHmac('sha256', key).update(message).digest('hex');
}

/** UTC 时间戳，格式 yyyy-MM-ddTHH:mm:ssZ */
function bceTimestamp(): string {
    return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function buildCanonicalQueryString(
    query: Record<string, string | number | undefined> | undefined,
): string {
    if (!query) return '';
    return Object.entries(query)
        .filter(([key, value]) => value !== undefined && key.toLowerCase() !== 'authorization')
        .map(([key, value]) => `${uriEncode(key)}=${uriEncode(String(value))}`)
        .sort()
        .join('&');
}

/**
 * 生成携带 bce-auth-v1 签名的请求头（Host / x-bce-date / Authorization）。
 * 签名依赖时间戳与完整 URL，必须每次请求前现算；Content-Type 不参与签名，由调用方自行附加。
 */
export function buildBceAuthHeaders(params: BceSignParams): Record<string, string> {
    const { ak, sk, method, path, query, host } = params;
    const expiration = params.expirationSeconds ?? 1800;
    const timestamp = bceTimestamp();

    // 1. 前缀与派生签名密钥
    const authStringPrefix = `bce-auth-v1/${ak}/${timestamp}/${expiration}`;
    const signingKey = hmacSha256Hex(sk, authStringPrefix);

    // 2. 规范请求串
    const canonicalUri = uriEncode(path.startsWith('/') ? path : `/${path}`, false);
    const canonicalQueryString = buildCanonicalQueryString(query);
    const signedHeaderList = [
        ['host', host],
        ['x-bce-date', timestamp],
    ] as const;
    const canonicalHeaders = signedHeaderList
        .map(([name, value]) => `${uriEncode(name)}:${uriEncode(value.trim())}`)
        .sort()
        .join('\n');
    const signedHeaders = signedHeaderList.map(([name]) => name).join(';');

    const canonicalRequest = [method, canonicalUri, canonicalQueryString, canonicalHeaders].join('\n');

    // 3. 签名与最终认证串
    const signature = hmacSha256Hex(signingKey, canonicalRequest);
    const authorization = `${authStringPrefix}/${signedHeaders}/${signature}`;

    return {
        Host: host,
        'x-bce-date': timestamp,
        Authorization: authorization,
    };
}
